// ** React Imports
import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'

// ** Store & Actions
import { useSelector, useDispatch } from 'react-redux'
import { handleMenuHidden, handleContentWidth } from '@store/layout'

// ** Third Party Components
import classnames from 'classnames'
import { ArrowUp, X } from 'react-feather'

// ** Reactstrap Imports
import { Navbar, NavItem, Button } from 'reactstrap'

// ** Configs
import themeConfig from '@configs/themeConfig'

// ** Custom Components

import Customizer from '@components/customizer'
import ScrollToTop from '@components/scrolltop'
import NavbarComponent from './components/navbar'
import FooterComponent from './components/footer'
import MenuComponent from './components/menu/horizontal-menu'

// ** Custom Hooks
import { useRTL } from '@hooks/useRTL'
import { useSkin } from '@hooks/useSkin'
import { useLayout } from '@hooks/useLayout'
import { useNavbarType } from '@hooks/useNavbarType'
import { useFooterType } from '@hooks/useFooterType'
import { useNavbarColor } from '@hooks/useNavbarColor'
import { useRouterTransition } from '@hooks/useRouterTransition'

// ** Styles
import '@styles/base/core/menu/menu-types/horizontal-menu.scss'

import KathokLogoLogo from '../../assets/images/backgrounds/Kathok-system-header.jpg'
import KathokLogo from '../../assets/images/logo/logo-kathok.png'

const HorizontalLayout = (props) => {
  // ** Props
  const { navbar, menuData, footer, children, menu } = props

  // ** Hooks
  const { skin, setSkin } = useSkin()
  const [isRtl, setIsRtl] = useRTL()
  const { navbarType, setNavbarType } = useNavbarType()
  const { footerType, setFooterType } = useFooterType()
  const { navbarColor, setNavbarColor } = useNavbarColor()
  const { layout, setLayout, setLastLayout } = useLayout()
  const { transition, setTransition } = useRouterTransition()

  // ** States
  const [isMounted, setIsMounted] = useState(false)
  const [navbarScrolled, setNavbarScrolled] = useState(false)
  const [menuVisibility, setMenuVisibility] = useState(false)

  // ** Store Vars
  const dispatch = useDispatch()
  const layoutStore = useSelector((state) => state.layout)

  // ** Vars
  const contentWidth = layoutStore.contentWidth
  const isHidden = layoutStore.menuHidden

  // ** Handles Content Width
  const setContentWidth = (val) => dispatch(handleContentWidth(val))

  // ** Handles Content Width
  const setIsHidden = (val) => dispatch(handleMenuHidden(val))

  // ** UseEffect Cleanup
  const cleanup = () => {
    setIsMounted(false)
    setNavbarScrolled(false)
  }

  // ** Vars
  const location = useLocation()

  //** ComponentDidMount
  useEffect(() => {
    setIsMounted(true)
    window.addEventListener('scroll', function () {
      if (window.pageYOffset > 65 && navbarScrolled === false) {
        setNavbarScrolled(true)
      }
      if (window.pageYOffset < 65) {
        setNavbarScrolled(false)
      }
    })
    return () => cleanup()
  }, [])

  // ** Close menu on route change
  useEffect(() => {
    if (menuVisibility) {
      setMenuVisibility(false)
    }
  }, [location])

  // ** Vars
  const footerClasses = {
    static: 'footer-static',
    sticky: 'footer-fixed',
    hidden: 'footer-hidden'
  }

  const navbarWrapperClasses = {
    floating: 'navbar-floating',
    sticky: 'navbar-sticky',
    static: 'navbar-static'
  }

  const navbarClasses = {
    floating: contentWidth === 'boxed' ? 'floating-nav container-xxl' : 'floating-nav',
    sticky: 'fixed-top'
  }

  const bgColorCondition = navbarColor !== '' && navbarColor !== 'light' && navbarColor !== 'white'

  if (!isMounted) {
    return null
  }

  return (
    <div className={classnames(`wrapper horizontal-layout horizontal-menu ${navbarWrapperClasses[navbarType] || 'navbar-floating'} ${footerClasses[footerType] || 'footer-static'} menu-expanded`)} {...(isHidden ? { 'data-col': '1-column' } : {})}>
      <Navbar
        expand="lg"
        container={false}
        className={classnames('header-navbar navbar-fixed align-items-center navbar-shadow ', {
          'navbar-scrolled': navbarScrolled
        })}
        // style={{ height: '120px' }}
      >
        <div className="navbar-container content">{navbar ? navbar({ skin, setSkin, setMenuVisibility }) : <NavbarComponent skin={skin} setSkin={setSkin} setMenuVisibility={setMenuVisibility} />}</div>
      </Navbar>

      <div
        className={classnames('banner-image')}
        style={{
          zIndex: 99999,
          left: 0,
          right: 0,
          position: 'fixed'
        }}
      >
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            backgroundColor: '#fff'
          }}
        >
          <img src={KathokLogoLogo} alt="logo" style={{ height: 120, marginLeft: 'auto', marginRight: 'auto' }} />
        </div>
      </div>
      {!isHidden ? (
        <div className="horizontal-menu-wrapper">
          <Navbar
            tag="div"
            expand="sm"
            light={skin !== 'dark'}
            dark={skin === 'dark' || bgColorCondition}
            className={classnames(`header-navbar navbar-horizontal navbar-shadow menu-border`, {
              [navbarClasses[navbarType]]: navbarType !== 'static',
              'floating-nav': (!navbarClasses[navbarType] && navbarType !== 'static') || navbarType === 'floating'
            })}
          >
            {menu ? menu({ menuData, routerProps, currentActiveItem }) : <MenuComponent menuData={menuData} />}
          </Navbar>
        </div>
      ) : null}

      {children}

      {/* Mobile Menu Overlay */}
      <div
        className={classnames('sidenav-overlay', {
          show: menuVisibility
        })}
        onClick={() => setMenuVisibility(false)}
      ></div>

      {/* Mobile Menu Sidebar */}
      {menuVisibility && (
        <div
          className="mobile-menu-wrapper"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '280px',
            height: '100vh',
            backgroundColor: '#fff',
            zIndex: 100000,
            boxShadow: '2px 0 10px rgba(0,0,0,0.1)',
            overflowY: 'auto',
            transform: menuVisibility ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 0.3s ease'
          }}
        >
          {/* Header with Logo */}
          <div className="p-3 d-flex justify-content-between align-items-center border-bottom">
            <div className="d-flex align-items-center">
              <img
                src={KathokLogo}
                alt="Kathok Logo"
                style={{
                  height: '40px',
                  width: '40px',
                  borderRadius: '50%',
                  marginRight: '10px',
                  objectFit: 'cover',
                  padding: '2px',
                  border: '2px solid #ff6b35'
                }}
              />
              <div>
                <h5 className="mb-0" style={{ color: '#5e50ee', fontWeight: 'bold', fontSize: '1.1rem' }}>Kathok</h5>
                <small style={{ color: '#6c757d', fontSize: '0.85rem' }}>Membership</small>
              </div>
            </div>
            <Button color="flat-secondary" className="btn-icon p-0" onClick={() => setMenuVisibility(false)}>
              <X size={20} style={{ color: '#5e50ee' }} />
            </Button>
          </div>
          {/* Menu Items */}
          <div className="p-2">{menu ? menu({ menuData }) : <MenuComponent menuData={menuData} />}</div>
        </div>
      )}

      {themeConfig.layout.customizer === true ? <Customizer skin={skin} isRtl={isRtl} layout={layout} setSkin={setSkin} setIsRtl={setIsRtl} isHidden={isHidden} setLayout={setLayout} footerType={footerType} navbarType={navbarType} transition={transition} setIsHidden={setIsHidden} themeConfig={themeConfig} navbarColor={navbarColor} contentWidth={contentWidth} setTransition={setTransition} setFooterType={setFooterType} setNavbarType={setNavbarType} setLastLayout={setLastLayout} setNavbarColor={setNavbarColor} setContentWidth={setContentWidth} /> : null}
      <footer
        className={classnames(`footer footer-light ${footerClasses[footerType] || 'footer-static'}`, {
          'd-none': footerType === 'hidden'
        })}
      >
        {footer ? footer : <FooterComponent footerType={footerType} footerClasses={footerClasses} />}
      </footer>

      {themeConfig.layout.scrollTop === true ? (
        <div className="scroll-to-top">
          <ScrollToTop showOffset={300} className="scroll-top d-block">
            <Button className="btn-icon" color="primary">
              <ArrowUp size={14} />
            </Button>
          </ScrollToTop>
        </div>
      ) : null}
    </div>
  )
}
export default HorizontalLayout
